import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, Square, X, Loader2, FileAudio2, Lock, AudioLines, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

let _statusPromise = null;
export function fetchVoiceStatus(force = false) {
  if (!_statusPromise || force) _statusPromise = api.get("/voice/status").then((r) => r.data).catch(() => null);
  return _statusPromise;
}
export function resetVoiceStatus() { _statusPromise = null; }

const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
const pickMime = () => (typeof MediaRecorder !== "undefined" && MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported?.(m))) || "";
const extFor = (mime) => (mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm");
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const buzz = (ms) => { try { navigator.vibrate?.(ms); } catch { /* no haptics */ } };
const HOLD_MS = 350;

function useCoarsePointer() {
  const q = "(max-width: 640px), (pointer: coarse)";
  const [coarse, setCoarse] = useState(() => typeof window !== "undefined" && window.matchMedia?.(q).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(q); if (!mq) return;
    const fn = (e) => setCoarse(e.matches);
    mq.addEventListener?.("change", fn);
    return () => mq.removeEventListener?.("change", fn);
  }, []);
  return !!coarse;
}

export function VoiceUpsellCard({ testid, onDismiss, where }) {
  return (
    <div className="mt-3 ss-card p-4 flex gap-3 items-start border border-gold/40" data-testid={`${testid}-upsell`}>
      <AudioLines size={18} className="text-gold shrink-0 mt-0.5" />
      <div className="flex-1 text-sm text-cocoa/80">
        <p className="font-heading text-lg text-cocoa">Say it instead of typing it.</p>
        <p className="mt-1 leading-relaxed">Voice notes are a Luxe feature. Record in the moment — or upload a voice memo — and SugarSense transcribes it, tidies the grammar, and places it here for you to tweak before you submit.</p>
        <div className="mt-3 flex items-center gap-3">
          <Link to="/billing?plan=luxe_monthly" data-testid={`${testid}-upsell-cta`} onClick={() => track("voice_upsell_cta_clicked", { where })} className="ss-pill ss-pill-gold h-9 text-xs"><Sparkles size={12} /> See Luxe</Link>
          <button type="button" onClick={onDismiss} className="text-xs text-cocoa/55 hover:text-cocoa" data-testid={`${testid}-upsell-dismiss`}>Not now</button>
        </div>
      </div>
    </div>
  );
}

// Luxe-only voice input. Renders a compact toolbar meant to sit directly beneath a textarea.
// onText(text) receives the cleaned transcript; the caller decides how to merge it.
// On phones (coarse pointer / narrow) the mic becomes a large hold-to-talk control.
export function VoiceInput({ onText, context, testid = "voice", label = "Speak it", nudge = false }) {
  const [status, setStatus] = useState(undefined);
  const [phase, setPhase] = useState("idle"); // idle | recording | transcribing
  const [secs, setSecs] = useState(0);
  const [upsell, setUpsell] = useState(false);
  const [holding, setHolding] = useState(false);
  const coarse = useCoarsePointer();
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const fileRef = useRef(null);
  const pressRef = useRef({ t: 0, mode: "tap" });

  useEffect(() => { fetchVoiceStatus().then(setStatus); }, []);
  useEffect(() => () => { stopTimer(); recRef.current?.stream?.getTracks?.().forEach((t) => t.stop()); }, []);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  const canRecord = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined";
  const enabled = !!status?.enabled;
  const maxSecs = status?.max_seconds || 600;

  const showUpsell = () => { setUpsell((v) => !v); track("voice_upsell_viewed", { where: testid }); };

  const send = async (blob, filename, source) => {
    setPhase("transcribing");
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      fd.append("source", source);
      if (context) fd.append("context", context);
      const r = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      if (r.data?.empty || !r.data?.text) {
        toast.message("Nothing came through", { description: "The recording had no clear speech. Try again a little closer to the mic." });
      } else {
        onText(r.data.text);
        buzz(20);
        toast.success("Transcribed", { description: "Read it over, tweak anything, then submit when it sounds like you." });
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402) { setUpsell(true); resetVoiceStatus(); fetchVoiceStatus(true).then(setStatus); }
      else toast.error(typeof d === "string" ? d : d?.message || "Couldn't transcribe that — try again or type it instead.");
    } finally { setPhase("idle"); }
  };

  const start = async () => {
    if (!enabled) { showUpsell(); return false; }
    if (!canRecord) { fileRef.current?.click(); return false; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = []; cancelledRef.current = false;
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        stopTimer();
        const type = rec.mimeType || mime || "audio/webm";
        if (cancelledRef.current) { setPhase("idle"); return; }
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 1024) { setPhase("idle"); toast.message("Too short", { description: "Hold a moment longer and tell it the way you'd tell a friend." }); return; }
        send(blob, `voice-note.${extFor(type)}`, "mic");
      };
      recRef.current = rec;
      rec.start(1000);
      buzz(12);
      setSecs(0); setPhase("recording");
      timerRef.current = setInterval(() => setSecs((s) => {
        if (s + 1 >= maxSecs) { rec.state !== "inactive" && rec.stop(); }
        return s + 1;
      }), 1000);
      return true;
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "Microphone access was blocked. Allow it in your browser, or upload a voice note instead." : "Couldn't start the microphone on this device — upload a voice note instead.");
      return false;
    }
  };
  const stop = () => { const r = recRef.current; if (r && r.state !== "inactive") { r.stop(); buzz(12); } };
  const cancel = () => { cancelledRef.current = true; stop(); setPhase("idle"); };
  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!enabled) { setUpsell(true); return; }
    if (f.size > (status?.max_mb || 20) * 1024 * 1024) { toast.error(`That file is over ${status?.max_mb || 20} MB. Trim it or record a shorter note.`); return; }
    send(f, f.name || "voice-note.m4a", "upload");
  };

  // Hold-to-talk (mobile): press starts, release stops. A quick tap toggles instead so one-handed use still works.
  const onPressStart = async (e) => {
    if (phase === "transcribing") return;
    if (recRef.current?.state === "recording") { stop(); return; }
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pressRef.current = { t: Date.now(), down: true };
    setHolding(true);
    await start();
    // released while the permission prompt was up → behave like a tap (keep recording until tapped again)
    if (!pressRef.current.down) setHolding(false);
  };
  const onPressEnd = () => {
    if (!pressRef.current.down) return;
    pressRef.current.down = false;
    setHolding(false);
    const held = Date.now() - pressRef.current.t;
    if (held >= HOLD_MS && recRef.current?.state === "recording") stop();
    // shorter than HOLD_MS → treated as a tap: leave it recording until tapped again
  };

  if (status === null) return null;
  const pill = "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs transition-colors";
  const lockBits = !enabled && <Lock size={10} className="ml-0.5 text-gold" />;
  const uploadBtn = (cls) => (
    <button type="button" onClick={() => (enabled ? fileRef.current?.click() : showUpsell())}
      data-testid={`${testid}-upload`} aria-label="Upload a voice note" className={cls}>
      <FileAudio2 size={13} /> Upload voice note{lockBits}
    </button>
  );
  const fileInput = <input ref={fileRef} type="file" accept="audio/*,.m4a,.mp3,.wav,.webm,.mp4,.ogg,.aac" className="hidden" onChange={onFile} data-testid={`${testid}-file-input`} />;

  if (coarse && phase !== "transcribing") {
    const rec = phase === "recording";
    return (
      <div className="mt-3" data-testid={`${testid}-toolbar`} data-layout="mobile">
        <div className="flex items-center gap-4">
          <button type="button" data-testid={`${testid}-mic`} aria-label={rec ? "Stop recording" : `Hold to ${label.toLowerCase()}`} aria-pressed={rec}
            onPointerDown={onPressStart} onPointerUp={onPressEnd} onPointerCancel={onPressEnd} onPointerLeave={onPressEnd}
            onContextMenu={(e) => e.preventDefault()}
            style={{ touchAction: "none", WebkitTouchCallout: "none" }}
            className={`relative shrink-0 w-[72px] h-[72px] rounded-full flex items-center justify-center select-none transition-transform active:scale-95 ${
              rec ? "bg-rose text-ivory shadow-[0_0_0_10px_rgba(223,166,160,0.25)]" : enabled ? "bg-mauve text-ivory shadow-md" : "bg-champagne/70 text-cocoa/60"}`}>
            {rec && <span className="absolute inset-0 rounded-full animate-ping bg-rose/40" />}
            {rec ? <Square size={22} fill="currentColor" className="relative" /> : <Mic size={26} className="relative" />}
            {!enabled && <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-ivory border border-gold flex items-center justify-center"><Lock size={11} className="text-gold" /></span>}
          </button>
          <div className="flex-1 min-w-0">
            {rec ? (
              <>
                <p className="text-sm text-rose font-medium" data-testid={`${testid}-recording`} aria-live="polite">Listening · {fmt(secs)}</p>
                <p className="text-xs text-cocoa/55 mt-0.5">{holding ? "Release to finish" : "Tap the button when you're done"}</p>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={stop} data-testid={`${testid}-stop`} className={`${pill} bg-cocoa text-ivory`}><Square size={11} fill="currentColor" /> Done</button>
                  <button type="button" onClick={cancel} data-testid={`${testid}-cancel`} className={`${pill} text-cocoa/60`}><X size={13} /> Discard</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-cocoa font-medium">
                  {enabled ? `Hold to ${label.toLowerCase()}` : label}
                  {!enabled && <span className="ml-2 text-[10px] tracking-[0.25em] uppercase text-gold" data-testid={`${testid}-luxe-tag`}>Luxe</span>}
                </p>
                <p className="text-xs text-cocoa/55 mt-0.5">{enabled ? "Or tap once to start, tap again to finish." : "Say it instead of typing — a Luxe feature."}</p>
                <div className="mt-2">{uploadBtn(`${pill} border border-champagne text-cocoa/70`)}</div>
              </>
            )}
          </div>
        </div>
        {fileInput}
        {nudge && !enabled && !upsell && (
          <button type="button" onClick={showUpsell} data-testid={`${testid}-nudge`} className="mt-2 text-xs text-mauve underline text-left">That's a lot to type — on Luxe you could just say it.</button>
        )}
        {upsell && !enabled && <VoiceUpsellCard testid={testid} where={testid} onDismiss={() => setUpsell(false)} />}
      </div>
    );
  }

  return (
    <div className="mt-2" data-testid={`${testid}-toolbar`} data-layout={coarse ? "mobile" : "desktop"}>
      <div className="flex items-center gap-2 flex-wrap">
        {phase === "recording" ? (
          <>
            <span className={`${pill} bg-rose/10 text-rose`} data-testid={`${testid}-recording`} aria-live="polite">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose opacity-60" /><span className="relative inline-flex rounded-full h-2 w-2 bg-rose" /></span>
              Listening · {fmt(secs)}
            </span>
            <button type="button" onClick={stop} data-testid={`${testid}-stop`} className={`${pill} bg-cocoa text-ivory hover:bg-cocoa/90`}>
              <Square size={11} fill="currentColor" /> Done
            </button>
            <button type="button" onClick={cancel} data-testid={`${testid}-cancel`} className={`${pill} text-cocoa/60 hover:text-cocoa`}>
              <X size={13} /> Discard
            </button>
          </>
        ) : phase === "transcribing" ? (
          <span className={`${pill} bg-champagne/70 text-cocoa`} data-testid={`${testid}-transcribing`} aria-live="polite">
            <Loader2 size={13} className="animate-spin" /> Transcribing &amp; tidying…
          </span>
        ) : (
          <>
            <button type="button" onClick={start} data-testid={`${testid}-mic`} aria-label={label}
              className={`${pill} border ${enabled ? "border-mauve/50 text-mauve hover:bg-mauve/10" : "border-champagne text-cocoa/55 hover:text-cocoa"}`}>
              <Mic size={13} /> {label}{lockBits}
            </button>
            {uploadBtn(`${pill} border ${enabled ? "border-champagne text-cocoa/70 hover:bg-champagne/40" : "border-champagne text-cocoa/55 hover:text-cocoa"}`)}
            {!enabled && <span className="text-[11px] tracking-[0.25em] uppercase text-gold" data-testid={`${testid}-luxe-tag`}>Luxe</span>}
            {fileInput}
          </>
        )}
      </div>
      {nudge && !enabled && !upsell && phase === "idle" && (
        <button type="button" onClick={showUpsell} data-testid={`${testid}-nudge`} className="mt-2 text-xs text-mauve underline">That's a lot to type — on Luxe you could just say it.</button>
      )}
      {upsell && !enabled && <VoiceUpsellCard testid={testid} where={testid} onDismiss={() => setUpsell(false)} />}
    </div>
  );
}

// Dashboard teaser for non-Luxe members. Dismiss is remembered per browser.
export function VoiceTeaser() {
  const [status, setStatus] = useState(undefined);
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem("ss_voice_teaser_dismissed") === "1"; } catch { return false; } });
  useEffect(() => { fetchVoiceStatus().then(setStatus); }, []);
  useEffect(() => { if (status && !status.enabled && !hidden) track("voice_upsell_viewed", { where: "dashboard" }); }, [status, hidden]);
  if (hidden || !status || status.enabled) return null;
  const dismiss = () => { try { localStorage.setItem("ss_voice_teaser_dismissed", "1"); } catch { /* private mode */ } setHidden(true); };
  return (
    <section data-testid="dashboard-voice-teaser" className="mt-6 ss-card-premium p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <span className="shrink-0 w-12 h-12 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold"><Mic size={20} /></span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.4em] uppercase text-gold">New on Luxe</p>
          <p className="font-heading text-xl sm:text-2xl text-cocoa mt-1 leading-tight">Some moments are easier said than typed.</p>
          <p className="text-sm text-cocoa/70 mt-1 leading-relaxed">Record a voice note in the moment — or upload one from your phone — and SugarSense transcribes it, tidies the grammar, and hands it back for you to tweak before you ask.</p>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <Link to="/billing?plan=luxe_monthly" data-testid="dashboard-voice-teaser-cta" onClick={() => track("voice_upsell_cta_clicked", { where: "dashboard" })} className="ss-pill ss-pill-gold h-10 text-xs"><Sparkles size={12} /> See Luxe</Link>
            <button type="button" onClick={dismiss} className="text-xs text-cocoa/55 hover:text-cocoa" data-testid="dashboard-voice-teaser-later">Maybe later</button>
          </div>
        </div>
        <button type="button" onClick={dismiss} aria-label="Dismiss" data-testid="dashboard-voice-teaser-dismiss" className="shrink-0 text-cocoa/40 hover:text-cocoa"><X size={14} /></button>
      </div>
    </section>
  );
}

export default VoiceInput;
